import React from 'react';
import { Box, SxProps } from '@mui/material';
import { Header } from 'common/ui/Header';
import { Paths } from 'services/navigation/paths';
import { AnalyseTemplateForm } from 'modules/analyse-templates/components/AnalyseTemplateForm';
import { AnalyseTemplateFormValues } from 'modules/analyse-templates/types/AnalyseTemplateFormValues';
import { validateAnalysisAutomationTasks } from 'modules/analyse/utils/validateAutomationTasks';
import { AutomationTaskInput } from 'generated-graphql/types';
import { ToastType, useToast } from 'services/toast';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useUser } from 'core/stores/hooks/useUser';
import { FailedMessageWithHref } from 'common/ui';
import { useCreateAutomationTasksTemplateMutation } from 'generated-graphql/hooks/apollo-hooks';
import { useGoBack } from 'common/hooks/useGoBack';
import { useSetDefaultTemplate } from 'hooks/useSetDefaultTemplate';

const container: SxProps = {
  p: 3
};

export const AnalyseTemplateCreationPage = () => {
  const { showToast } = useToast();
  const { handleGoBack } = useGoBack(Paths.ANALYSE_TEMPLATES);

  const organization = useOrganization();
  const user = useUser();
  const { setDefaultTemplate } = useSetDefaultTemplate();

  const [createAutomationTasksTemplateMutation] =
    useCreateAutomationTasksTemplateMutation();

  const onFormCompleted = async ({
    templateName,
    ruleAutomationTasks,
    actionAutomationTasks,
    setAsDefault
  }: AnalyseTemplateFormValues) => {
    const automationTasks = [...actionAutomationTasks, ...ruleAutomationTasks];

    try {
      const { isDuplicates, validAutomationTasks } =
        validateAnalysisAutomationTasks(automationTasks || [], true);

      if (isDuplicates) {
        showToast({
          message:
            'You have conflicting automations. Remove them before saving.',
          type: ToastType.Error
        });
        return;
      }

      const formattedAutomationTasks =
        validAutomationTasks.map<AutomationTaskInput>((item) => {
          return {
            action: item.action,
            requirementConditions: item.requirementConditions,
            isParent: item.isParent
          };
        });

      const result = await createAutomationTasksTemplateMutation({
        variables: {
          input: {
            title: templateName,
            automationTasks: formattedAutomationTasks,
            organizationId: organization.id,
            userId: user.id
          }
        }
      });

      if (setAsDefault && result.data?.createAutomationTasksTemplate?.id) {
        await setDefaultTemplate(result.data.createAutomationTasksTemplate.id);
      }

      handleGoBack();

      showToast({
        title: 'Template added',
        type: ToastType.Success
      });
    } catch (e) {
      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage="Try creating the template again."
            emailSubject="Failed to create template"
          />
        ),
        type: ToastType.Error
      });
    }
  };

  return (
    <Box sx={container}>
      <Header
        breadcrumbs={[{ name: 'Analyse', path: Paths.ANALYSE }]}
        currentBreadcrumb="Templates"
        title="Templates"
        items={['Reusable settings for use across analyses']}
      />
      <AnalyseTemplateForm
        onFormSubmitted={onFormCompleted}
        onCanceled={handleGoBack}
        submitButtonLabel="Save template"
      />
    </Box>
  );
};
